import Skeleton from '@mui/material/Skeleton';
import { integrationTesting } from '@ps/web-core-utils';
import type { Reference } from '@ps/web-martech-cms-types';
import type { SportsReference } from '@ps/web-sports-page-types';
import classNames from 'clsx';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import useDebouncedSelector from '@swf/core/hooks/useDebouncedSelector';
import { ThemeContext } from '@swf/core/providers/ThemeProvider';
import { select } from '@swf/core/store';

import CMSReferences from '../../CMSReference/CMSReferences';
import AuthRequiredPage from '../AuthRequiredPage/AuthRequiredPage';

import styles from './PageBody.scss';

interface Props {
  twoColumns: boolean;
  darkTheme: boolean;
  main: Reference[] | SportsReference[];
  side: Reference[] | SportsReference[];
}

const PageBody: React.FC<React.PropsWithChildren<Props>> = ({
  twoColumns,
  main,
  side,
  darkTheme,
  children
}) => {
  const authenticated = useSelector(select.session.isAuthenticated);
  const isServerAuthenticated = useSelector(select.session.isServerAuthenticated);
  const authRequired = useSelector(select.cms.isAuthRequired);
  const { darkClassName } = useContext(ThemeContext);
  const { hash } = useLocation();
  const isEmbeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const loading = useDebouncedSelector(select.cms.getPageLoading, integrationTesting ? 1000 : 0);
  const alias = useSelector(select.cms.getPage)?.alias;
  const activeAlias = useSelector(select.cms.getActiveAlias);
  const staticPage = !(alias || activeAlias);

  useEffect(() => {
    const scrollToAnchor = () => {
      if (!loading && hash) {
        const element = document.getElementById(hash.replace('#', ''));
        element?.scrollIntoView();
      }
    };

    if (document.readyState === 'complete') {
      scrollToAnchor();
    } else {
      window.addEventListener('load', scrollToAnchor);
      return () => window.removeEventListener('load', scrollToAnchor);
    }
  }, [loading, hash]);

  if (authRequired && !authenticated) {
    return <AuthRequiredPage />;
  }

  if (staticPage && (loading || (authRequired && isServerAuthenticated))) {
    return (
      <div className={styles.mainSkeleton}>
        <Skeleton variant="rectangular" animation="wave" component="h1" />
        <Skeleton variant="rectangular" animation="wave" component="h2" width="80%" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" width="80%" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" width="80%" />
        <Skeleton variant="rectangular" animation="wave" component="h2" width="80%" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" width="80%" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" />
        <Skeleton variant="text" animation="wave" width="80%" />
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.body, darkTheme && darkClassName, {
        [styles.twoColumns]: twoColumns,
        [styles.embeddedContainer]: isEmbeddedRoute
      })}
    >
      {main && (
        <div className={styles.main}>
          <CMSReferences references={main} />
        </div>
      )}
      {twoColumns && side && (
        <aside className={styles.side}>
          <CMSReferences references={side} />
        </aside>
      )}
      {!main.length && children}
    </div>
  );
};

export default PageBody;
